import { useCallback, useEffect, useState } from 'react'
import { Dialog } from '@headlessui/react'
import {
  ArrowUpTrayIcon,
  PencilSquareIcon,
  XMarkIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'
import { boxApi } from '../utils/api'
import { useAuth } from '../context/AuthContext'

export default function MemberList({ boxId, canManage = false, organizerId }) {
  const { currentUser } = useAuth()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editValues, setEditValues] = useState({ name: '', email: '', notes: '', position: '', contribution: '', payoutsPerCycle: '' })
  const [removeReason, setRemoveReason] = useState('')
  const [actionError, setActionError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [manageMember, setManageMember] = useState(null)
  const [addingSelf, setAddingSelf] = useState(false)

  const loadMembers = useCallback(async () => {
    try {
      setLoading(true)
      const data = await boxApi.getMembers(boxId)
      setMembers(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [boxId])

  useEffect(() => {
    loadMembers()
  }, [loadMembers])

  const closeDialog = () => {
    setManageMember(null)
    setActionError(null)
    setSubmitting(false)
    setRemoveReason('')
  }

  const openDialog = (member) => {
    const currentIndex =
      typeof member.join_order === 'number'
        ? member.join_order
        : Math.max(
            0,
            members.findIndex((m) => m.id === member.id)
          )
    setManageMember(member)
    setEditValues({
      name: member.name || '',
      email: member.email || '',
      notes: member.notes || '',
      position: (currentIndex + 1).toString(),
      contribution: member.contribution_amount ?? '',
      payoutsPerCycle: member.desired_payouts_per_cycle ?? '',
    })
    setRemoveReason('')
    setActionError(null)
    setSubmitting(false)
  }

  const handleEditSubmit = async (event) => {
    event.preventDefault()
    if (!manageMember) return
    setActionError(null)

    const desiredPosition = parseInt(editValues.position, 10)
    if (Number.isNaN(desiredPosition) || desiredPosition < 1) {
      setActionError('Position must be 1 or greater.')
      return
    }
    if (desiredPosition > members.length) {
      setActionError(`Position must be between 1 and ${members.length}.`)
      return
    }
    if (editValues.contribution !== '' && Number.isNaN(parseFloat(editValues.contribution))) {
      setActionError('Contribution must be a number or left blank.')
      return
    }
    if (editValues.payoutsPerCycle !== '' && (Number.isNaN(parseInt(editValues.payoutsPerCycle, 10)) || parseInt(editValues.payoutsPerCycle, 10) < 1)) {
      setActionError('Payouts per cycle must be a positive integer or left blank.')
      return
    }

    const getPositionNumber = (member) => {
      if (!member) return null
      if (typeof member.join_order === 'number') return member.join_order + 1
      const idx = members.findIndex((m) => m.id === member.id)
      return idx >= 0 ? idx + 1 : null
    }

    const currentPosition = getPositionNumber(manageMember) ?? desiredPosition
    const conflictingMember =
      desiredPosition !== currentPosition
        ? members.find((m) => m.id !== manageMember.id && getPositionNumber(m) === desiredPosition)
        : null

    setSubmitting(true)
    try {
      if (conflictingMember && currentPosition) {
        await boxApi.updateMember(boxId, conflictingMember.id, { join_order: currentPosition - 1 })
      }

      const payload = {
        name: editValues.name,
        email: editValues.email,
        notes: editValues.notes,
        join_order: desiredPosition - 1,
      }
      if (editValues.contribution !== '') {
        payload.contribution_amount = parseFloat(editValues.contribution)
      }
      if (editValues.payoutsPerCycle !== '') {
        payload.desired_payouts_per_cycle = parseInt(editValues.payoutsPerCycle, 10)
      } else {
        // Clear the field if left blank (use default)
        payload.desired_payouts_per_cycle = null
      }
      await boxApi.updateMember(boxId, manageMember.id, payload)
      await loadMembers()
      closeDialog()
    } catch (err) {
      setActionError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handlePromote = async () => {
    if (!manageMember || !canManage || manageMember.user_id === organizerId || !manageMember.user_id) {
      return
    }
    setSubmitting(true)
    setActionError(null)
    try {
      await boxApi.promoteMember(boxId, manageMember.id)
      await loadMembers()
      closeDialog()
    } catch (err) {
      setActionError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async (event) => {
    event.preventDefault()
    if (!manageMember || !canManage) return
    const isOrganizer = manageMember.user_id && manageMember.user_id === organizerId
    if (isOrganizer) {
      setActionError('You cannot remove the organizer.')
      return
    }
    const reason = removeReason.trim()
    if (!reason) {
      setActionError('Reason is required')
      return
    }
    setSubmitting(true)
    setActionError(null)
    try {
      await boxApi.removeMember(boxId, manageMember.id, { reason })
      await loadMembers()
      closeDialog()
    } catch (err) {
      setActionError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddSelf = async () => {
    setAddingSelf(true)
    setError(null)
    try {
      await boxApi.addSelfToBox(boxId)
      await loadMembers()
    } catch (err) {
      setError(err.message)
    } finally {
      setAddingSelf(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading members...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <p className="text-red-800">Error loading members: {error}</p>
      </div>
    )
  }

  const isOwnerMember = members.some((member) => member.user_id === currentUser?.uid)
  const showAddSelfButton = canManage && !isOwnerMember

  if (members.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center">
        <p className="text-gray-500">No members yet. Invite people to join this box!</p>
        {showAddSelfButton && (
          <button
            onClick={handleAddSelf}
            disabled={addingSelf}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlusIcon className="mr-2 h-5 w-5" />
            {addingSelf ? 'Adding...' : 'Add Yourself to Box'}
          </button>
        )}
      </div>
    )
  }

  const selectedIsOrganizer = manageMember?.user_id && manageMember.user_id === organizerId
  const manageCanPromote = canManage && manageMember?.user_id && manageMember.user_id !== organizerId
  const manageCanRemove = canManage && !selectedIsOrganizer

  return (
    <>
      <div className="bg-white shadow rounded-2xl border border-gray-100">
        {showAddSelfButton && (
          <div className="px-6 py-4 border-b border-gray-200 bg-primary-50">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-primary-900 font-medium">
                You are the organizer but not a member of this box yet.
              </p>
              <button
                onClick={handleAddSelf}
                disabled={addingSelf}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserPlusIcon className="mr-2 h-5 w-5" />
                {addingSelf ? 'Adding...' : 'Add Yourself to Box'}
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {canManage && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contribution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.map((member) => {
                const isOrganizer = member.user_id && member.user_id === organizerId

                return (
                  <tr key={member.id}>
                    {canManage && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => openDialog(member)}
                          className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-semibold text-primary-700 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        >
                          <PencilSquareIcon className="mr-2 h-4 w-4" />
                          Edit
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{member.name || '—'}</div>
                          {isOrganizer && (
                            <span className="mt-1 inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
                              Organizer
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{member.email || '—'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {member.contribution_amount !== undefined && member.contribution_amount !== null
                          ? `$${Number(member.contribution_amount).toFixed(2)}`
                          : '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        #{(member.join_order || 0) + 1}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manage member dialog */}
      <Dialog as="div" open={!!manageMember} onClose={closeDialog} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-xl rounded-3xl bg-white p-5 sm:p-6 shadow-2xl max-h-[82vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Dialog.Title className="text-lg font-semibold text-gray-900">Manage member</Dialog.Title>
                <p className="mt-1 text-sm text-gray-500">
                  Update their details, promote to organizer, or remove them from this box.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                className="rounded-full p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            {manageMember && (
              <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm">
                <div className="font-semibold text-gray-900">{manageMember.name || '—'}</div>
                <div className="text-gray-600">{manageMember.email || '—'}</div>
              </div>
            )}
            {actionError && <p className="mt-3 text-sm text-red-600">{actionError}</p>}

            <div className="mt-6 space-y-8">
              <form className="space-y-4 rounded-2xl border border-gray-100 p-4" onSubmit={handleEditSubmit}>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Edit details</h3>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {submitting ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={editValues.name}
                      onChange={(e) => setEditValues((prev) => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={editValues.email}
                      onChange={(e) => setEditValues((prev) => ({ ...prev, email: e.target.value }))}
                      className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    rows={3}
                    value={editValues.notes || ''}
                    onChange={(e) => setEditValues((prev) => ({ ...prev, notes: e.target.value }))}
                    className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contribution amount ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editValues.contribution}
                    onChange={(e) => setEditValues((prev) => ({ ...prev, contribution: e.target.value }))}
                    className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Defaults to box amount"
                  />
                  <p className="mt-2 text-xs text-gray-500">Leave blank to keep the box default contribution.</p>
                </div>
                {canManage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Desired payouts per cycle</label>
                    <input
                      type="number"
                      min="1"
                      value={editValues.payoutsPerCycle}
                      onChange={(e) => setEditValues((prev) => ({ ...prev, payoutsPerCycle: e.target.value }))}
                      className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="Auto (use box default)"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Number of payouts this member wants in the cycle period. Leave blank to use box default distribution.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Position</label>
                  <input
                    type="number"
                      min="1"
                      max={members.length}
                      value={editValues.position}
                      onChange={(e) => setEditValues((prev) => ({ ...prev, position: e.target.value }))}
                      className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      If another member already has this position, their position will swap with this one.
                    </p>
                  </div>
                </div>
              </form>

              <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 p-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Organizer</h3>
                  <p className="text-sm text-gray-500">
                    Promote this member to organizer. You will still remain a member of the box.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handlePromote}
                  disabled={!manageCanPromote || submitting}
                  className={`inline-flex w-fit items-center rounded-full px-4 py-2 text-sm font-semibold ${
                    manageCanPromote
                      ? 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <ArrowUpTrayIcon className="mr-2 h-4 w-4" />
                  {submitting ? 'Promoting…' : 'Promote to organizer'}
                </button>
                {!manageCanPromote && (
                  <p className="text-xs text-gray-400">Only non-organizer members can be promoted.</p>
                )}
              </div>

              <form
                className="space-y-3 rounded-2xl border border-red-100 bg-red-50/60 p-4"
                onSubmit={handleRemove}
              >
                <div>
                  <h3 className="text-sm font-semibold text-red-700">Remove from box</h3>
                  <p className="text-sm text-red-600">
                    Removing a member requires a reason. All ledger contributions must be voided before removal.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Reason<span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={3}
                    value={removeReason}
                    onChange={(e) => setRemoveReason(e.target.value)}
                    className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    required
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={!manageCanRemove || submitting}
                    className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold ${
                      manageCanRemove
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    } disabled:opacity-50`}
                  >
                    {submitting ? 'Removing…' : 'Remove member'}
                  </button>
                </div>
              </form>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={closeDialog}
                className="text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  )
}
